// File: frontend/src/components/EnhancedWeekView.js

import { useMemo, useState, useCallback, memo } from 'react';
import { ChevronLeft, ChevronRight, Moon } from 'lucide-react';
import { habitFill, formatLocalDate } from '../utils/colorUtils';
import GradientProgressOverlay from './GradientProgressOverlay';
import HabitsEmptyState from './HabitsEmptyState';
import { getWeekStart } from '../utils/weekCalculations';
import { useAnimations } from '../context/AnimationContext';

/**
 * Generates a random organic border-radius string
 * to create hand-drawn/irregular shapes.
 */
const getRandomBorderRadius = () => {
  const min = 40;
  const max = 60;

  const tl = Math.floor(Math.random() * (max - min) + min);
  const br = Math.floor(Math.random() * (max - min) + min);

  const tl2 = Math.floor(Math.random() * (max - min) + min);
  const tr2 = Math.floor(Math.random() * (max - min) + min);
  const br2 = Math.floor(Math.random() * (max - min) + min);
  const bl2 = Math.floor(Math.random() * (max - min) + min);

  return `${tl}% ${100 - tl}% ${br}% ${100 - br}% / ${tl2}% ${tr2}% ${br2}% ${bl2}%`;
};

/**
 * Generates a random delay for breathing animations
 */
const getRandomAnimationDelay = () => {
  return `${Math.random() * -4}s`;
};

function EnhancedWeekView({
  habits,
  weekDates,
  completions,
  sickWeeks = {},
  onToggle,
  onToggleSick,
  isSmall,
  weekLabel,
  streaks,
  isCurrentWeek,
  layoutMode = 'standard',
  onPreviousWeek,
  onNextWeek,
  onCurrentWeek,
  canGoToNextWeek,
  enableAnimations = false,
  embedded = false, // when true, size to content (no internal scroll) — used by the marketing homepage demo
  config: configProp, // Received from parent
  onEmptySuggest, // optional: shown only when habits.length === 0; opens the starter-habits modal
  onEmptyAddOwn, // optional: shown only when habits.length === 0; opens the settings modal at add-habit
}) {
  const { reduceAnimations } = useAnimations();
  // Decorative tap effects (ripple rings, ink-burst, ink-dissolve ghost) all
  // depend on running keyframes. When Reduce Animations is on, `.reduce-animations`
  // kills animations globally — those ghost/ripple divs would otherwise mount
  // as static colored overlays stuck in their first keyframe until the 800-
  // 1200ms cleanup timeout, producing a visible "off → on → off" flash on
  // uncheck. Gate the whole effect pipeline on the user's setting.
  const effectsEnabled = enableAnimations && !reduceAnimations;
  const config = configProp || {
    layout: 'stacked', buttonSize: 'h-12 w-12', textSize: 'text-base',
    dayTextSize: 'text-xs', padding: 'p-4', showWeekLabel: true,
    showProgress: true, nameColWidth: '120px'
  };
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const today = formatLocalDate(new Date());

  // Get week start for sick weeks lookup
  const weekStart = weekDates.length > 0 ? getWeekStart(new Date(weekDates[0])) : null;
  const weekStartStr = weekStart ? formatLocalDate(weekStart) : null;

  // Helper to check if a habit is sick for this week
  const isHabitSick = useCallback((habitId) => {
    return weekStartStr && sickWeeks[`${habitId}-${weekStartStr}`];
  }, [sickWeeks, weekStartStr]);

  // Handle sick toggle
  const handleSickToggle = useCallback((habitId) => {
    if (onToggleSick && weekStart) {
      onToggleSick(habitId, weekStart);
    }
  }, [onToggleSick, weekStart]);

  // Track recently completed/uncompleted habits for animations
  const [recentlyCompleted, setRecentlyCompleted] = useState(new Set());
  const [recentlyUnchecked, setRecentlyUnchecked] = useState(new Set());

  // Generate random styles for dots only when habits or weekDates change
  // We use JSON.stringify to ensure stability across renders if the content hasn't changed
  const weekDatesKey = JSON.stringify(weekDates);
  const habitIdsKey = habits.map(h => h.id).join(',');

  const dotStyles = useMemo(() => {
    const styles = {};
    habits.forEach(habit => {
      weekDates.forEach(date => {
        const key = `${habit.id}-${date}`;
        styles[key] = {
          borderRadius: getRandomBorderRadius(),
          animationDelay: getRandomAnimationDelay()
        };
      });
    });
    return styles;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habitIdsKey, weekDatesKey]);

  const handleToggle = useCallback((habitId, date) => {
    const key = `${habitId}-${date}`;
    const wasCompleted = completions[key];

    if (effectsEnabled) {
      if (!wasCompleted) {
        // Completing - trigger burst animation
        setRecentlyCompleted(prev => new Set([...prev, key]));
        setTimeout(() => {
          setRecentlyCompleted(prev => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }, 1200); // Match slower animation duration
      } else {
        // Unchecking - trigger dissolve animation
        setRecentlyUnchecked(prev => new Set([...prev, key]));
        setTimeout(() => {
          setRecentlyUnchecked(prev => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }, 800); // Match dissolve animation duration
      }
    }

    // Call the original toggle
    if (onToggle) {
      onToggle(habitId, date);
    }
  }, [completions, effectsEnabled, onToggle]);

  // Helper for mobile stacked layout
  const renderStackedLayout = () => (
    <div className="space-y-4">
      {/* Day Headers */}
      <div className="flex justify-between space-x-2">
        {(() => {
          const sizeMatch = config.buttonSize?.match(/[hw]-(\d+)/);
          const sizeRem = sizeMatch ? parseInt(sizeMatch[1]) * 0.25 : 3.5;
          const sizePx = `${sizeRem}rem`;

          return dayNames.map((day, index) => (
            <div key={day} className="text-center flex-shrink-0" style={{ width: sizePx }}>
              <div className={`
                    ${config.dayTextSize} uppercase tracking-widest font-sans font-semibold
                    ${weekDates[index] === today ? 'text-[var(--color-accent-warm)]' : 'text-[var(--color-ink-subtle)]'}
                  `}>
                {layoutMode === 'compact' ? day.charAt(0) : day.slice(0, 3)}
              </div>
            </div>
          ));
        })()}
      </div>

      {/* Habits Stack — more vertical breathing room in portrait where the
          week view has the vertical budget now that yearDots is capped compact. */}
      <div className="space-y-3 portrait:space-y-6 relative">
        <GradientProgressOverlay
            habits={habits}
            weekDates={weekDates}
            completions={completions}
            sickWeeks={sickWeeks}
            isMobile={true}
            layoutMode={layoutMode}
          />
        {habits.map(habit => {
          const weekCount = weekDates.filter(date => completions[`${habit.id}-${date}`]).length;
          const isSick = isHabitSick(habit.id);

          return (
            <div key={habit.id} data-habit-row="" className="relative z-10 group">
              {/* Header */}
              <div className="flex justify-between items-center mb-1 px-1">
                <div className="flex items-center space-x-1.5">
                  <span className={`font-serif font-medium text-[var(--color-ink-main)] ${config.textSize} ${isSick ? 'opacity-60' : ''}`}>
                    {habit.name}
                  </span>
                  {/* Sick/Rest Toggle Button */}
                  <button
                    onClick={() => handleSickToggle(habit.id)}
                    className={`p-0.5 rounded-full transition-all duration-200 ${
                      isSick
                        ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                        : 'opacity-30 hover:opacity-100 active:opacity-100 text-[var(--color-ink-subtle)] hover:text-amber-500 hover:bg-amber-50 active:bg-amber-50 dark:hover:bg-amber-900/20'
                    }`}
                    aria-label={isSick ? `Remove rest status from ${habit.name}` : `Mark ${habit.name} as resting this week`}
                    title={isSick ? 'Click to resume tracking' : 'Mark as resting this week'}
                  >
                    <Moon size={12} className={isSick ? 'fill-current' : ''} />
                  </button>
                  {isSick && (
                    <span className="text-[0.625rem] font-sans text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                      resting
                    </span>
                  )}
                </div>
                {config.showProgress && (
                  <span className="text-xs font-sans text-[var(--color-ink-light)]">
                    {isSick ? <span className="text-amber-600">rest</span> : `${weekCount}/${habit.weekly_goal}`}
                  </span>
                )}
              </div>

              {/* Dots Row */}
              <div className="flex justify-between space-x-2">
                {weekDates.map(date => {
                  const isCompleted = completions[`${habit.id}-${date}`];
                  const isToday = date === today;
                  const key = `${habit.id}-${date}`;
                  const style = dotStyles[key] || {};
                  const justCompleted = recentlyCompleted.has(key);
                  const justUnchecked = recentlyUnchecked.has(key);

                  // Parse buttonSize to get pixel values (e.g., "h-20 w-20" -> 80px)
                  const sizeMatch = config.buttonSize?.match(/[hw]-(\d+)/);
                  const sizeRem = sizeMatch ? parseInt(sizeMatch[1]) * 0.25 : 3.5; // Default to 14 (3.5rem)
                  const sizePx = `${sizeRem}rem`;

                  return (
                    <div
                      key={date}
                      className="relative flex-shrink-0"
                      style={{ width: sizePx, height: sizePx }}
                    >
                      {/* Ripple rings that expand outward on completion */}
                      {justCompleted && (
                        <>
                          <div
                            className="absolute top-0 left-0 right-0 bottom-0 rounded-full border-2 animate-ripple-ring pointer-events-none"
                            style={{
                              borderColor: 'var(--color-accent-warm)',
                              borderRadius: style.borderRadius
                            }}
                          />
                          <div
                            className="absolute top-0 left-0 right-0 bottom-0 rounded-full border-2 animate-ripple-ring-delayed pointer-events-none"
                            style={{
                              borderColor: 'var(--color-accent-warm)',
                              opacity: 0.5,
                              borderRadius: style.borderRadius
                            }}
                          />
                        </>
                      )}
                      {/* Inward ripple on unchecking */}
                      {justUnchecked && (
                        <div
                          className="absolute top-0 left-0 right-0 bottom-0 rounded-full border-2 animate-ripple-inward pointer-events-none"
                          style={{
                            borderColor: 'var(--color-ink-subtle)',
                            borderRadius: style.borderRadius
                          }}
                        />
                      )}
                      {/* Ghost of the completed dot that dissolves away */}
                      {justUnchecked && (
                        <div
                          className={`absolute top-0 left-0 right-0 bottom-0 ${habitFill(habit.color).className} animate-ink-dissolve pointer-events-none mix-blend-multiply dark:mix-blend-screen`}
                          style={{ ...habitFill(habit.color).style, borderRadius: style.borderRadius }}
                        />
                      )}
                      <button
                        onClick={() => handleToggle(habit.id, date)}
                        className={`
                          flex items-center justify-center
                          transition-colors duration-300 ease-out
                          ${isCompleted
                            ? `${habitFill(habit.color).className} ${justCompleted ? 'animate-ink-burst' : ''} shadow-md scale-105`
                            : `hover:scale-110 active:scale-95 ${isToday && enableAnimations ? 'animate-shimmer hover:animate-none' : ''}`
                          }
                        `}
                        style={{
                          width: '100%', height: '100%',
                          border: isCompleted ? 'none' : '2px solid ' + (isToday ? 'var(--color-accent-warm)' : 'var(--color-ink-subtle)'),
                          backgroundColor: isCompleted ? undefined : 'var(--color-surface-input)',
                          ...(isCompleted ? habitFill(habit.color).style : undefined),
                          borderRadius: style.borderRadius,
                          animationDelay: enableAnimations && !justCompleted ? style.animationDelay : '0s',
                          ...(isToday && !isCompleted ? { boxShadow: '0 0 0 4px rgba(212, 165, 116, 0.25)' } : {})
                        }}
                        aria-label={`Toggle ${habit.name} for ${date}`}
                      >
                        {isCompleted && (
                          <div className="week-day-gloss w-full h-full rounded-full bg-white/20 blur-[0.0625rem] mix-blend-overlay" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Helper for desktop grid layout (uses flexbox for Chrome 42+ compat)
  const renderGridLayout = () => (
    <div className="overflow-visible">
      {/* Headers */}
      <div className="flex items-center mb-4 landscape:mb-0.5">
        <div style={{ width: config.nameColWidth, flexShrink: 0 }}></div>
        {dayNames.map((day, index) => (
          <div key={day} className="text-center" style={{ flex: 1, marginLeft: index === 0 ? '1rem' : '0.5rem' }}>
            <span className={`
                  ${config.dayTextSize} uppercase tracking-widest font-sans font-semibold
                  ${weekDates[index] === today ? 'text-[var(--color-accent-warm)]' : 'text-[var(--color-ink-subtle)]'}
                `}>
              {day}
            </span>
          </div>
        ))}
        {config.showProgress && <div style={{ width: '5rem', flexShrink: 0, marginLeft: '0.5rem' }}></div>}
      </div>

      {/* Habits Grid */}
      <div className={`${config.rowGap ?? 'space-y-4'} relative`}>
        <GradientProgressOverlay
            habits={habits}
            weekDates={weekDates}
            completions={completions}
            sickWeeks={sickWeeks}
            isMobile={false}
            layoutMode={layoutMode}
          />
        {habits.map(habit => {
          const weekCount = weekDates.filter(date => completions[`${habit.id}-${date}`]).length;
          const isSick = isHabitSick(habit.id);
          const goalMet = isSick || weekCount >= habit.weekly_goal;

          return (
            <div
              key={habit.id}
              data-habit-row=""
              className="flex items-center relative z-10 group"
            >
              {/* Habit Name with Rest Toggle */}
              <div className="flex items-center space-x-2 pr-4" style={{ width: config.nameColWidth, flexShrink: 0 }}>
                <span className={`font-serif font-medium text-[var(--color-ink-main)] ${config.textSize} truncate ${isSick ? 'opacity-60' : ''}`}>
                  {habit.name}
                </span>
                {/* Sick/Rest Toggle Button */}
                <button
                  onClick={() => handleSickToggle(habit.id)}
                  className={`p-1 rounded-full transition-all duration-200 flex-shrink-0 ${
                    isSick
                      ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                      : 'opacity-30 hover:opacity-100 active:opacity-100 text-[var(--color-ink-subtle)] hover:text-amber-500 hover:bg-amber-50 active:bg-amber-50 dark:hover:bg-amber-900/20'
                  }`}
                  aria-label={isSick ? `Remove rest status from ${habit.name}` : `Mark ${habit.name} as resting this week`}
                  title={isSick ? 'Click to resume tracking' : 'Mark as resting this week'}
                >
                  <Moon size={14} className={isSick ? 'fill-current' : ''} />
                </button>
                {isSick && (
                  <span className="text-[0.625rem] font-sans text-amber-600 dark:text-amber-400 uppercase tracking-wider flex-shrink-0">
                    resting
                  </span>
                )}
              </div>

              {/* Days */}
              {weekDates.map((date, index) => {
                const isCompleted = completions[`${habit.id}-${date}`];
                const isToday = date === today;
                const key = `${habit.id}-${date}`;
                const style = dotStyles[key] || {};
                const justCompleted = recentlyCompleted.has(key);
                const justUnchecked = recentlyUnchecked.has(key);

                return (
                  <div key={date} className="flex justify-center" style={{ flex: 1, marginLeft: index === 0 ? '1rem' : '0.5rem' }}>
                    <div className={`relative ${config.buttonSize}`}>
                      {/* Ripple rings that expand outward on completion */}
                      {justCompleted && (
                        <>
                          <div
                            className="absolute top-0 left-0 right-0 bottom-0 border-2 animate-ripple-ring pointer-events-none"
                            style={{
                              borderColor: 'var(--color-accent-warm)',
                              borderRadius: style.borderRadius
                            }}
                          />
                          <div
                            className="absolute top-0 left-0 right-0 bottom-0 border-2 animate-ripple-ring-delayed pointer-events-none"
                            style={{
                              borderColor: 'var(--color-accent-warm)',
                              opacity: 0.5,
                              borderRadius: style.borderRadius
                            }}
                          />
                        </>
                      )}
                      {/* Inward ripple on unchecking */}
                      {justUnchecked && (
                        <div
                          className="absolute top-0 left-0 right-0 bottom-0 border-2 animate-ripple-inward pointer-events-none"
                          style={{
                            borderColor: 'var(--color-ink-subtle)',
                            borderRadius: style.borderRadius
                          }}
                        />
                      )}
                      {/* Ghost of the completed dot that dissolves away */}
                      {justUnchecked && (
                        <div
                          className={`absolute top-0 left-0 right-0 bottom-0 ${habitFill(habit.color).className} animate-ink-dissolve pointer-events-none mix-blend-multiply dark:mix-blend-screen`}
                          style={{ ...habitFill(habit.color).style, borderRadius: style.borderRadius }}
                        />
                      )}
                      <button
                        onClick={() => handleToggle(habit.id, date)}
                        className={`
                          flex items-center justify-center
                          transition-colors duration-300 ease-out
                          ${isCompleted
                            ? `${habitFill(habit.color).className} ${justCompleted ? 'animate-ink-burst' : ''} shadow-md scale-110`
                            : `hover:scale-110 active:scale-95 ${isToday && enableAnimations ? 'animate-shimmer hover:animate-none' : ''}`
                          }
                        `}
                        style={{
                          width: '100%', height: '100%',
                          border: isCompleted ? 'none' : '2px solid ' + (isToday ? 'var(--color-accent-warm)' : 'var(--color-ink-subtle)'),
                          backgroundColor: isCompleted ? undefined : 'var(--color-surface-input)',
                          ...(isCompleted ? habitFill(habit.color).style : undefined),
                          borderRadius: style.borderRadius,
                          animationDelay: enableAnimations && !justCompleted ? style.animationDelay : '0s',
                          ...(isToday && !isCompleted ? { boxShadow: '0 0 0 4px rgba(212, 165, 116, 0.2)' } : {})
                        }}
                      >
                        {/* Watercolor texture detail */}
                        {isCompleted && (
                          <div className="week-day-gloss w-full h-full rounded-full bg-white/20 blur-[0.125rem] mix-blend-overlay" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Progress */}
              {config.showProgress && (
                <div className="flex justify-center" style={{ width: '5rem', flexShrink: 0, marginLeft: '0.5rem' }}>
                  <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                    isSick
                      ? 'border-amber-500/30 bg-amber-500/10 text-amber-600'
                      : goalMet
                        ? 'border-green-500/30 bg-green-500/10 text-green-700'
                        : 'border-[var(--color-ink-subtle)]/20 text-[var(--color-ink-light)]'
                  }`}>
                    {isSick ? (
                      <Moon size={16} className="fill-current" />
                    ) : (
                      <span className="text-sm font-serif font-bold">{weekCount}/{habit.weekly_goal}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={`
      w-full ${embedded ? '' : 'h-full'} flex flex-col
      glass-morphism rounded-3xl backdrop-blur-xl
      ${config.padding}
      bg-[var(--color-paper-warm)]/40 border border-[var(--color-ink-subtle)]/10
      shadow-sm hover:shadow-md transition-shadow duration-500
    `}>
      {/* Header with Navigation. Tight in landscape — week label reads fine
          at text-base, and mb-6 was pushing the grid way down on 411-px tall
          phone-landscape viewports. */}
      <div className="flex items-center justify-between mb-6 landscape:mb-0 flex-shrink-0">
        <div className="flex items-center space-x-4">
          {config.showWeekLabel && (
            <h3 className={`font-serif font-bold text-[var(--color-ink-main)] ${layoutMode === 'expanded' ? 'text-2xl' : 'text-xl landscape:text-base'}`}>
              {weekLabel}
            </h3>
          )}

          {/* Nav Controls */}
          <div className="flex items-center space-x-1">
            <button onClick={onPreviousWeek} className="p-1 hover:bg-[var(--color-ink-main)]/5 rounded-full transition-colors">
              <ChevronLeft className="w-5 h-5 text-[var(--color-ink-light)]" />
            </button>
            <button
              onClick={onNextWeek}
              disabled={!canGoToNextWeek}
              className={`p-1 rounded-full transition-colors ${!canGoToNextWeek ? 'opacity-30 cursor-not-allowed' : 'hover:bg-[var(--color-ink-main)]/5'}`}
            >
              <ChevronRight className="w-5 h-5 text-[var(--color-ink-light)]" />
            </button>
            {!isCurrentWeek && (
              <button onClick={onCurrentWeek} className="ml-2 text-xs font-sans font-bold text-[var(--color-accent-warm)] hover:text-[var(--color-ink-main)] transition-colors uppercase tracking-wider">
                Return to Today
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content Area. pl-8/pr-8 eats 64 CSS px of horizontal room, which
          was OK when buttons were 32-40px but is too expensive now that the
          phone-portrait base is h-14. Drop to pl-2/pr-2 so the checkbox
          columns actually get enough width to render the buttons without
          cutting off Sunday. md: restores the roomier padding on tablets+. */}
      <div className={`${embedded ? '' : 'flex-1 min-h-0 overflow-y-auto landscape:overflow-y-hidden overflow-x-hidden custom-scrollbar'} pl-2 pr-2 md:pl-8 md:pr-8 py-4 landscape:py-0`}>
        {habits.length === 0 && (onEmptySuggest || onEmptyAddOwn) ? (
          <HabitsEmptyState onSuggest={onEmptySuggest} onAddOwn={onEmptyAddOwn} />
        ) : config.layout === 'stacked' ? renderStackedLayout() : renderGridLayout()}
      </div>
    </div>
  );
}

export default memo(EnhancedWeekView);
